// Global Instructions Rule Applied!
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Card, Descriptions, Tag, Typography } from 'antd'
import { fetchFundraisingById } from '../../../../app_modules/fundraising/services/fundraisingService.ts'
import { selectProspectById } from '../../../../state_manager/state_update_actions/fundraising/selectors.ts'
import { fetchTasksForProspect } from '../../../../app_modules/tasks/services/tasksService.ts'
import { fetchInteractionsForProspect } from '../../../../app_modules/interactions/services/interactionsService.ts'
import { useRecordDetail } from '../lib/useRecordDetail.ts'
import { useRecordRecent } from '../lib/useRecordRecent.ts'
import { useSectionData } from '../lib/useSectionData.ts'
import { detailPath } from '../lib/routes.ts'
import { text, dateText } from '../lib/format.ts'
import { compactMoney } from '../lib/money.ts'
import { LoadingState, ErrorState, Section, SectionSpinner, SectionError } from '../components/ui.tsx'
import { TaskList } from '../components/TaskList.tsx'
import { InteractionList } from '../components/InteractionList.tsx'

const EMPTY_CURRENCIES: any[] = []

/**
 * Prospect detail (read-only) — the fundraising twin of DealScreen. Reached via the desktop
 * deep-link `/fundraising/forms/full/:id` (the module is `fundraising`, not `prospects`).
 * Reuses `fetchFundraisingById` (slice-first via useRecordDetail) for the record, then
 * per-section lazy fetches over the existing services: tasks (`fetchTasksForProspect`, with
 * the record's embedded `prog__tasks` as first paint) and recent activity
 * (`fetchInteractionsForProspect`). No Files section — there is no `file_urls_prospects`.
 */
export function ProspectScreen(): React.JSX.Element {
  const { id } = useParams()
  const navigate = useNavigate()
  const currencies = useSelector((s: any) => s?.globalConfigs?.currencies ?? EMPTY_CURRENCIES)
  const { record: prospect, loading, error } = useRecordDetail<any>(id, selectProspectById, fetchFundraisingById)
  useRecordRecent('fundraising', id, prospect?.accounts__name, prospect?.funds__name)

  const tasksState = useSectionData<any[]>(id, (key, token) =>
    fetchTasksForProspect(key, { include_completed: true }, token)
  )
  const activityState = useSectionData<any[]>(id, (key, token) => fetchInteractionsForProspect(key, undefined, token))

  if (loading && !prospect) return <LoadingState label="Loading prospect…" />
  if (!prospect) return <ErrorState message={error} />

  const fallbackTasks: any[] = Array.isArray(prospect.prog__tasks) ? prospect.prog__tasks : []
  const taskRows: any[] = tasksState.data ?? fallbackTasks
  const currencyId = prospect.prospects__currency
  const stageColour = prospect.pipeline_stages__colour
  const stageLabel = prospect.pipeline_prospects__stage__label

  const hasDetails =
    prospect.prospects__commitment != null ||
    prospect.prospects__allocation_available != null ||
    prospect.pipeline_stages__decision_status__label != null ||
    prospect.pipeline_prospects__stage_date != null

  return (
    <div style={{ padding: 12 }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 18, lineHeight: 1.25 }}>
          {prospect.accounts__id ? (
            <Typography.Link onClick={() => navigate(detailPath('companies', prospect.accounts__id))}>
              {text(prospect.accounts__name)}
            </Typography.Link>
          ) : (
            text(prospect.accounts__name)
          )}
        </div>
        {prospect.funds__name ? (
          <div style={{ marginTop: 4 }}>
            {prospect.funds__id ? (
              <Typography.Link onClick={() => navigate(detailPath('funds', prospect.funds__id))}>
                {text(prospect.funds__name)}
              </Typography.Link>
            ) : (
              <span style={{ color: 'rgba(0,0,0,0.45)' }}>{text(prospect.funds__name)}</span>
            )}
          </div>
        ) : null}
        {stageLabel ? (
          <div style={{ marginTop: 10 }}>
            <Tag color={stageColour || 'default'} style={stageColour ? { color: '#fff', borderColor: stageColour } : undefined}>
              {text(stageLabel)}
            </Tag>
          </div>
        ) : null}
      </Card>

      {hasDetails ? (
        <Section title="Overview">
          <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '45%' }}>
            {prospect.prospects__commitment != null ? (
              <Descriptions.Item label="Commitment">
                {compactMoney(prospect.prospects__commitment, currencyId, currencies)}
              </Descriptions.Item>
            ) : null}
            {prospect.prospects__allocation_available != null ? (
              <Descriptions.Item label="Allocation available">
                {compactMoney(prospect.prospects__allocation_available, currencyId, currencies)}
              </Descriptions.Item>
            ) : null}
            {prospect.pipeline_stages__decision_status__label ? (
              <Descriptions.Item label="Decision status">
                {text(prospect.pipeline_stages__decision_status__label)}
              </Descriptions.Item>
            ) : null}
            {prospect.pipeline_prospects__stage_date ? (
              <Descriptions.Item label="Stage since">{dateText(prospect.pipeline_prospects__stage_date)}</Descriptions.Item>
            ) : null}
          </Descriptions>
        </Section>
      ) : null}

      <Section title="Tasks">
        {tasksState.data === null && tasksState.loading && fallbackTasks.length === 0 ? (
          <SectionSpinner />
        ) : tasksState.data === null && tasksState.error && fallbackTasks.length === 0 ? (
          <SectionError message="Couldn't load tasks." onRetry={tasksState.retry} />
        ) : (
          <TaskList tasks={taskRows} />
        )}
      </Section>

      <Section title="Recent activity">
        {activityState.data === null && activityState.loading ? (
          <SectionSpinner />
        ) : activityState.data === null && activityState.error ? (
          <SectionError message="Couldn't load activity." onRetry={activityState.retry} />
        ) : (
          <InteractionList interactions={activityState.data ?? []} />
        )}
      </Section>
    </div>
  )
}

export default ProspectScreen
